import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { Input } from '@/components/ui/Input';
import { authApi } from '@/api/auth';

/**
 * Login pixel-perfect CTO-FRONT-002.
 * Design : carte centrée sur fond teinté filiale (Leadbeton vert par défaut),
 * titre Bricolage Grotesque, bouton gradient brand, fond `var(--bg)`.
 */
export const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, error, accessToken, clearError } = useAuthStore();
  const [email, setEmail]       = useState('daf@leadbeton.ci');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd]   = useState(false);
  const [oubliEnCours, setOubliEnCours] = useState(false);
  const [oubliMessage, setOubliMessage] = useState<string | null>(null);

  /**
   * Demande un lien de réinitialisation pour l'email saisi.
   *
   * Le serveur répond la même chose que le compte existe ou non, et l'écran
   * affiche cette réponse telle quelle : distinguer les deux cas ferait de ce
   * formulaire un annuaire des comptes de l'entreprise.
   */
  const demanderReinitialisation = async () => {
    setOubliMessage(null);
    if (!email.trim()) {
      setOubliMessage('Saisissez d\'abord votre email ci-dessus.');
      return;
    }
    setOubliEnCours(true);
    try {
      const { message } = await authApi.motDePasseOublie(email.trim());
      setOubliMessage(message);
    } catch {
      setOubliMessage(
        'Impossible de contacter le serveur pour le moment. Réessayez, ou demandez ' +
        'une réinitialisation à l\'administrateur du groupe.',
      );
    } finally {
      setOubliEnCours(false);
    }
  };

  if (accessToken) {
    const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname || '/';
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    try {
      const { requires2FA } = await login(email, password);
      if (requires2FA) {
        navigate('/login/2fa');
        return;
      }
      // SIMG-COMMERCIAL-LEADBETON-2026-001 + ACHATS-2026-002 — chaque rôle
      // atterrit sur son portail dédié plutôt que sur la HomePage admin.
      const role = useAuthStore.getState().user?.role;
      const landing = role === 'commercial' ? '/commercial'
                    : role === 'acheteur'   ? '/achats'
                    : '/';
      navigate(landing);
    } catch { /* erreur déjà capturée dans store */ }
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center p-4"
      style={{ background: 'var(--bg)' }}
    >
      <div
        className="w-full max-w-md p-10"
        style={{
          background: 'var(--surface)',
          border:     '1px solid var(--bdr)',
          borderRadius: 'var(--r-lg)',
          boxShadow: 'var(--sh-lg)',
        }}
      >
        <div className="mb-8 text-center">
          <img src="/assets/logos/simgroup-logo-officiel.svg" alt="SIM GROUP" className="mx-auto h-14 object-contain" />
        </div>

        <h1 className="font-d text-center" style={{ fontSize: 26, fontWeight: 700, color: 'var(--tx)' }}>
          Bienvenue
        </h1>
        <p className="mb-7 mt-1 text-center" style={{ fontSize: 13, color: 'var(--tx-3)' }}>
          Connectez-vous à votre espace SIM GROUP
        </p>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="mb-1.5 block text-xs font-medium" style={{ color: 'var(--tx-2)' }}>
              Email
            </label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="daf@leadbeton.ci"
            />
          </div>

          <div>
            <label htmlFor="password" className="mb-1.5 block text-xs font-medium" style={{ color: 'var(--tx-2)' }}>
              Mot de passe
            </label>
            <div className="relative">
              <Input
                id="password"
                type={showPwd ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1"
                style={{ color: 'var(--tx-3)' }}
                aria-label="Afficher / masquer le mot de passe"
              >
                {showPwd ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            </div>
          </div>

          {/* Ce lien était un `<a href="#">` sans traitement : il promettait
              une fonction qui n'existait pas. Un bouton qui ne fait rien est
              pire que pas de bouton — on croit avoir agi. */}
          <div className="text-right">
            <button
              type="button"
              className="text-xs font-medium hover:underline"
              style={{ color: 'var(--p)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              disabled={oubliEnCours}
              onClick={demanderReinitialisation}
            >
              {oubliEnCours ? 'Envoi…' : 'Mot de passe oublié ?'}
            </button>
          </div>

          {oubliMessage && (
            <div className="rounded-md border px-3 py-2 text-xs"
              style={{ background: '#f0f9ff', borderColor: '#bae6fd', color: '#075985' }}>
              {oubliMessage}
            </div>
          )}

          {error && (
            <div className="rounded-md border px-3 py-2 text-xs"
              style={{ background: '#fef2f2', borderColor: '#fecaca', color: '#7f1d1d' }}>
              {error.message}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="ds-btn ds-btn-p w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ padding: '12px 16px', fontSize: 14 }}
          >
            {isLoading ? 'Connexion…' : 'Se connecter →'}
          </button>
        </form>

        <div className="mt-7 text-center" style={{ fontSize: 11, color: 'var(--tx-3)' }}>
          Plateforme ERP · OHADA SYSCOHADA<br />
          © SIM GROUP 2026 · Confidentiel
        </div>
      </div>
    </div>
  );
};
